import React from 'react';
import { RouteComponentProps } from 'react-router';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import Helmet from 'helmet/helmet';
import Styled from 'styled';

import ScrollToTop from 'lib/scroll/scroll-to-top';
import {
    Halfpage,
    ZahradaDoubleSquare,
    ZahradaSquare,
    ZoznamHalfpageBottom,
    ZoznamHalfpageTop,
} from 'lib/google/dfp/ad-units/halfpage';
import { SectionCountConsumer } from 'lib/section-count-context/section-count-context';

import { Site } from 'four-nets/site';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import PrArticles from 'four-nets/widgets/pr-articles/pr-articles';
import Contest from 'four-nets/contest/contest';
import HotContentWidget from 'four-nets/cms/hot-content-widget';
import Staffpicks from 'four-nets/widgets/staffpicks/staffpicks';
import MostPopularPbAlbums from 'four-nets/widgets/most-popular-pb-albums/most-popular-pb-albums';
import FeaturedContent from 'four-nets/featured-content/featured-content';
import { ViewerFragment } from 'four-nets/user/viewer/viewer.graphql';

import MySelectionLoader from './my-selection/my-selection-loader';
import Hashtags from './hashtags/hashtags';
import FeedFilter from './feed/feed-filter/feed-filter';
import FeedLoader from './feed/feed-loader';
import Create from './create-post/create';
import LeftMenu from './left-menu/left-menu';
import defaultStyles from './photoblog.scss';
import { TryNewDemanderBox } from './try-new-demander';

interface Params {
    section?: string;
}

interface Search {
    selection?: string;
    search?: string;
    h?: string;
    offset?: number;
}

interface State {
    search?: string;
}

interface Props extends RouteComponentProps<Params> {
    styles: typeof defaultStyles;
    aside: boolean;
    search: Search;
}

export enum Section {
    FRIENDS = 'friends',
    MY_SELECTION = 'my-selection',
    PROFESSIONALS = 'professionals',
    MY_LIKES = 'my-likes',
    MY_COMMENTS = 'my-comments',
    IS_STAFFPICK = 'staff-picks',
    MOST_FAVORITE = 'most-favorite',
}

export const PhotoBlogSections = Object.keys(Section).map(k => Section[k as keyof typeof Section]);

class PhotoBlog extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    static defaultProps = {
        aside: true,
    };

    viewer?: ViewerFragment | null;
    state: State = { search: this.props.search.search || '' };

    setSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ search: event.target.value });
    };

    onSubmitSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        this.props.history.push({
            pathname: this.props.location.pathname,
            search: `?${queryString.stringify({
                search: this.state.search,
            })}`,
        });
    };

    renderHelmet = (H: React.ComponentClass<{}, any>) => {
        const { site } = this.context;

        const { match } = this.props;
        const {
            params: { section },
        } = match;

        const inLikedPhotoBlog = section === Section.FRIENDS;
        const mySelection = section === Section.MY_SELECTION;
        const professionals = section === Section.PROFESSIONALS;
        const myComments = section === Section.MY_COMMENTS;
        const myLikes = section === Section.MY_LIKES;
        const isStaffpick = section === Section.IS_STAFFPICK;
        const mostFavorite = section === Section.MOST_FAVORITE;

        if (mostFavorite) {
            return (
                <FormattedMessage id='PHOTOBLOGS'>
                    {message => (
                        <FormattedMessage id='FAVORITE_ALBUMS'>
                            {message2 => (
                                <H>
                                    <title>
                                        {message} - {message2} | {site.name}
                                    </title>
                                </H>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        } else if (inLikedPhotoBlog) {
            return (
                <FormattedMessage id='PHOTOBLOGS'>
                    {message => (
                        <FormattedMessage id='PB_MENU_FRIENDS_AND_FAVORITES'>
                            {message2 => (
                                <H>
                                    <title>
                                        {message} - {message2} | {site.name}
                                    </title>
                                </H>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        } else if (mySelection) {
            return (
                <FormattedMessage id='PHOTOBLOGS'>
                    {message => (
                        <FormattedMessage id='MY_SELECTION'>
                            {message2 => (
                                <H>
                                    <title>
                                        {message} - {message2} | {site.name}
                                    </title>
                                </H>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        } else if (professionals) {
            return (
                <FormattedMessage id='PHOTOBLOGS'>
                    {message => (
                        <FormattedMessage id='BUSINESS_DIRECTORY'>
                            {message2 => (
                                <H>
                                    <title>
                                        {message} - {message2} | {site.name}
                                    </title>
                                </H>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        } else if (myComments) {
            return (
                <FormattedMessage id='PHOTOBLOGS'>
                    {message => (
                        <FormattedMessage id='PB_MENU_MY_COMMENTS' values={{ gender: 'm' }}>
                            {message2 => (
                                <H>
                                    <title>
                                        {message} - {message2} | {site.name}
                                    </title>
                                </H>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        } else if (myLikes) {
            return (
                <FormattedMessage id='PHOTOBLOGS'>
                    {message => (
                        <FormattedMessage id='PB_MENU_MY_LIKES'>
                            {message2 => (
                                <H>
                                    <title>
                                        {message} - {message2} | {site.name}
                                    </title>
                                </H>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        } else if (isStaffpick) {
            return (
                <FormattedMessage id='PHOTOBLOGS'>
                    {message => (
                        <FormattedMessage id='STAFF_PICKS_TITLE'>
                            {message2 => (
                                <H>
                                    <title>
                                        {message} - {message2} | {site.name}
                                    </title>
                                </H>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        } else {
            return (
                <FormattedMessage id='PHOTOBLOGS'>
                    {message => (
                        <FormattedMessage id='PB_MENU_ALL_USERS'>
                            {message2 => (
                                <FormattedMessage id='PHOTOBLOG_INDEX_DESCRIPTION_LIVING'>
                                    {message3 => (
                                        <H>
                                            <title>
                                                {message} - {message2} | {site.name}
                                            </title>
                                            {site.isLiving ? (
                                                <meta name='Description' content={message3 as string} />
                                            ) : null}
                                        </H>
                                    )}
                                </FormattedMessage>
                            )}
                        </FormattedMessage>
                    )}
                </FormattedMessage>
            );
        }
    };

    render() {
        const { styles, match, search, aside, location } = this.props;
        const {
            params: { section },
        } = match;
        const { search: searchQS, h, selection, offset } = search || ({} as any);

        const { site } = this.context;

        const inLikedPhotoBlog = section === Section.FRIENDS;
        const mySelection = section === Section.MY_SELECTION;
        const professionals = section === Section.PROFESSIONALS;
        const myComments = section === Section.MY_COMMENTS;
        const myLikes = section === Section.MY_LIKES;
        const isStaffpick = section === Section.IS_STAFFPICK;
        const mostFavorite = section === Section.MOST_FAVORITE;

        /*
         * We need to refetch FeedLoader with the same variables and we can't determine queryKey variable (used for busting cache).
         * From Apollo Docs:
         * Please note that if you call refetchQueries with an array of strings, then Apollo Client will look for any previously
         * called queries that have the same names as the provided strings. It will then refetch those queries with their current variables.
         */
        const refetchQueries = ['FeedLoader'];

        const categories = JSON.stringify([
            inLikedPhotoBlog,
            mySelection,
            myComments,
            myLikes,
            professionals,
            mostFavorite,
        ]);

        return (
            <div className={styles.columns}>
                <SectionCountConsumer>
                    {({ sectionCounts }) => (
                        <ScrollToTop
                            scrollOnMount={false}
                            sectionBlogs={sectionCounts.SECTION_BLOGS}
                            categories={categories}
                            selection={selection}
                            h={h}
                            offset={offset}
                            searchQS={searchQS}
                            isStaffpick={isStaffpick}
                        />
                    )}
                </SectionCountConsumer>
                <Helmet render={this.renderHelmet} />
                <div>
                    <h1 className={styles.title}>
                        <FormattedMessage id='PHOTOBLOGS' />
                    </h1>
                    <LeftMenu />
                </div>
                <div className={styles.column}>
                    <FormattedMessage id='ADD_SHORT_MESSAGE' values={{ gender: true }}>
                        {msg => <Create placeholder={msg as string} refetchQueries={refetchQueries} />}
                    </FormattedMessage>
                    <FeedFilter />
                    <ViewerConsumer>
                        {({ viewer }) => {
                            this.viewer = viewer;
                            return [
                                mySelection && viewer ? (
                                    <MySelectionLoader key='my-selection-loader' refetchQueries={refetchQueries} />
                                ) : null,
                                <FeedLoader
                                    key='feed-loader'
                                    inLikedPhotoBlog={inLikedPhotoBlog}
                                    mySelection={mySelection}
                                    professionals={professionals}
                                    myLikes={myLikes}
                                    myComments={myComments}
                                    search={searchQS}
                                    isStaffpick={isStaffpick}
                                    hashtag={h}
                                    selection={selection}
                                    mostFavorite={mostFavorite}
                                    pathname={location.pathname}
                                    offset={offset}
                                    viewer={viewer}
                                    site={site}
                                />,
                            ];
                        }}
                    </ViewerConsumer>
                </div>
                {aside ? (
                    <aside className={styles.rightColumn}>
                        <Contest />
                        <HotContentWidget />
                        <FeaturedContent />
                        <Staffpicks style={{ wrapper: styles.staffpicks }} />
                        <TryNewDemanderBox />
                        <PrArticles />
                        <MostPopularPbAlbums defaultDayRange={3} />
                        <div>
                            {(() => {
                                if (site.isSlovak && site.isWedding) {
                                    return <ZoznamHalfpageTop />;
                                }
                                if (site.isGarden) {
                                    return <ZahradaSquare />;
                                }

                                return <Halfpage adUnitCode='halfpage-top' />;
                            })()}
                        </div>
                        <Hashtags />
                        <div className={styles.adWrapperSticky}>
                            {(() => {
                                if (site.isSlovak && site.isWedding) {
                                    return <ZoznamHalfpageBottom />;
                                }
                                if (site.isGarden) {
                                    return <ZahradaDoubleSquare />;
                                }

                                return <Halfpage adUnitCode='halfpage-bottom' />;
                            })()}
                        </div>
                    </aside>
                ) : null}
            </div>
        );
    }
}

export default Styled(defaultStyles)(PhotoBlog);
